<script setup lang="ts">
import { useGameStore } from '@/stores/gameStore'
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const gameStore = useGameStore()

const timeRemainingPercentage = ref(0)
const now = ref(new Date())
let countdownInterval: number | undefined

const gameHasStarted = computed(() => gameStore.turn > 1)

// Calculate time left until arena starts
const timeLeft = computed(() => {
  const startTime = new Date(gameStore.startTime)
  const totalSeconds = Math.max(0, Math.floor((startTime.getTime() - now.value.getTime()) / 1000))

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const pad = (n: number) => n.toString().padStart(2, "0")

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
})

const updateTimeRemainingPercentage = () => {
  const timeSinceTurn = Date.now() / 1000 - gameStore.dayTurnedAt
  const percentage = timeSinceTurn / ((gameStore.dayInterval * 60) / 100)
  timeRemainingPercentage.value = Math.min(100, Math.max(0, percentage))
}

const showDayToolTip = () => {
  gameStore.modalType = 'dayWeek'
}

onMounted(() => {
  // Update immediately and then every second
  updateTimeRemainingPercentage()
  const interval = setInterval(updateTimeRemainingPercentage, 1000)
  
  // Also update countdown timer if game hasn't started
  if (!gameHasStarted.value) {
    countdownInterval = window.setInterval(() => {
      now.value = new Date()
    }, 1000)
  }

  // Clear the interval when the component is unmounted
  onUnmounted(() => {
    clearInterval(interval)
    if (countdownInterval) {
      clearInterval(countdownInterval)
    }
  })
})
</script>

<template>
  <div class="flex items-center">
    <div
      v-if="!gameHasStarted"
      class="day-container relative overflow-hidden rounded cursor-pointer sm:min-w-28"
      @click="showDayToolTip"
    >
      <div class="py-0.5 px-2 relative z-10 text-center text-green-400 font-medium">
        {{ timeLeft }}
      </div>
    </div>

    <!-- Normal day progress bar for Day 2+ -->
    <div
      v-else
      class="day-container relative overflow-hidden rounded cursor-pointer sm:min-w-28"
      @click="showDayToolTip"
    >
      <div class="day-bg absolute inset-0"></div>
      <div
        :class="gameStore.shipCommandBlock ? 'day-progress-blocked' : 'day-progress'"
        class="absolute inset-0"
        :style="{ width: timeRemainingPercentage + '%' }"
        :title="gameStore.shipCommandBlock ? t('game.time.commandsBlocked') : ''"
      ></div>
      <div class="py-0.5 px-2 relative z-10 text-center">{{ t('game.time.day', { turn: gameStore.turn }) }}</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.day-container {
  background: linear-gradient(to top, rgba(15, 18, 22, 0.9), rgba(30, 35, 42, 0.85));
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.3);
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.day-bg {
  background: transparent;
}

.day-progress {
  background: linear-gradient(to top, rgba(40, 46, 56, 0.95), rgba(55, 63, 75, 0.9));
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.3);
}

.day-progress-blocked {
  background: linear-gradient(to top, rgba(30, 64, 120, 0.95), rgba(50, 100, 180, 0.9));
  border-right: 1px solid rgba(100, 180, 255, 0.2);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.3);
}
</style>